import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { useCart } from "@/lib/cart";
import { getCheckoutStatus } from "@/lib/api";

export default function OrderConfirmation() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState<"processing" | "paid" | "error">("processing");
  const [orderId, setOrderId] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    clearCart();
    let active = true;
    let attempts = 0;
    const check = async () => {
      try {
        const result = await getCheckoutStatus(sessionId);
        if (!active) return;
        if (result.status === "paid") { setStatus("paid"); setOrderId(result.orderId ? String(result.orderId) : null); return; }
        if (++attempts < 8) window.setTimeout(check, 1500);
      } catch { if (active) setStatus("error"); }
    };
    void check();
    return () => { active = false; };
  // `clearCart` is recreated by the cart provider on every cart update. Depending
  // on it here caused this effect to continuously clear the cart and block clicks.
  // A completed checkout only needs this work once per Stripe session.
  }, [sessionId]);

  return (
    <div className="order-result-page">
      <div className="container max-w-lg text-center">
        <div className="animate-fade-in-up">
          <CheckCircle
            size={64}
            className="mx-auto mb-6"
            style={{ color: "var(--color-sage)" }}
          />
          <h1
            className="text-3xl mb-3"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}
          >
            {status === "paid" ? t.orderConfirmation.title : t.orderConfirmation.processingTitle}
          </h1>
          <p className="text-lg mb-6" style={{ color: "var(--color-text-muted)" }}>
            {status === "paid" ? t.orderConfirmation.subtitle : t.orderConfirmation.processingSubtitle}
          </p>

          {orderId && status === "paid" && (
            <p className="text-sm mb-4" style={{ color: "var(--color-text-light)" }}>
              {t.orderConfirmation.orderNumber}: <strong>#{orderId}</strong>
            </p>
          )}

          <p className="text-sm mb-8" style={{ color: "var(--color-text-muted)" }}>
            {status === "paid" ? t.orderConfirmation.emailSent : status === "error" ? t.orderConfirmation.processingError : t.orderConfirmation.processingEmail}
          </p>

          <Link to="/gallery" className="btn-primary">
            {t.orderConfirmation.continueShopping}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
